import { useEffect, useMemo, useState } from "react";
import { Download, Filter as FilterIcon, RotateCcw } from "lucide-react";

import { Card } from "../../../shared/card/card.tsx";
import {
  Button,
  DateRange,
  Select,
  type SelectOption,
} from "../../../ui/index.ts";
import { formatToDateOnly } from "../../../../../utils/helpers.js";
import { useEmployeeNamesListStore } from "../../../../../stores/employeeNamesListStore.js";
import type { ExtensionReportFilters as ExtensionReportFilterValues } from "../types.ts";

import styles from "./extension-report-filters.module.css";

export interface ExtensionReportFiltersProps {
  value: ExtensionReportFilterValues;
  onApply: (filters: ExtensionReportFilterValues) => void;
  onReset: () => void;
  onExport: (filters: ExtensionReportFilterValues) => void;
  loading?: boolean;
  exporting?: boolean;
}

interface EmployeeOption {
  value: string | number;
  label: string;
}

function toSelectValue(id: number | null | undefined): string {
  return id == null ? "" : String(id);
}

function parseDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function ExtensionReportFilters({
  value,
  onApply,
  onReset,
  onExport,
  loading,
  exporting,
}: ExtensionReportFiltersProps) {
  const { employeeList, fetchEmployees } = useEmployeeNamesListStore();

  const [employeeId, setEmployeeId] = useState<string>(
    toSelectValue(value.employeeId),
  );
  const [startDate, setStartDate] = useState<Date | null>(
    parseDate(value.fromDate),
  );
  const [endDate, setEndDate] = useState<Date | null>(parseDate(value.toDate));
  const [status, setStatus] = useState(value.status);

  useEffect(() => {
    if (employeeList.length === 0) {
      void fetchEmployees();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const peopleOptions: SelectOption[] = useMemo(
    () =>
      (employeeList as EmployeeOption[]).map((o) => ({
        value: String(o.value),
        label: o.label,
      })),
    [employeeList],
  );

  const buildFilters = (): ExtensionReportFilterValues => ({
    employeeId: employeeId ? Number(employeeId) : null,
    fromDate: startDate ? formatToDateOnly(startDate) : null,
    toDate: endDate ? formatToDateOnly(endDate) : null,
    status,
  });

  const handleApply = () => onApply(buildFilters());

  const handleReset = () => {
    setEmployeeId("");
    setStartDate(null);
    setEndDate(null);
    setStatus("");
    onReset();
  };

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setStartDate(start || null);
    setEndDate(end || null);
  };

  return (
    <Card className={styles.card}>
      <div className={styles.row}>
        <div className={styles.field}>
          <Select
            label="Employee Name"
            placeholder="Select Employee Name"
            options={peopleOptions}
            value={employeeId}
            onChange={setEmployeeId}
            isClearable
            searchable
          />
        </div>

        <div className={styles.field}>
          <Select
            label="Status"
            placeholder="Select Status"
            options={[
              { value: "PENDING", label: "Pending" },
              { value: "APPROVED", label: "Approved" },
              { value: "REJECTED", label: "Rejected" },
            ]}
            value={status}
            onChange={(next) => setStatus(next as ExtensionReportFilterValues["status"])}
            isClearable
          />
        </div>

        <div className={styles.field}>
          <DateRange
            label="Date Range"
            startDate={startDate}
            endDate={endDate}
            onChange={handleDateChange}
            placeholderText="Select Date Range"
          />
        </div>

        <div className={styles.actions}>
          <Button
            variant="accent"
            type="button"
            onClick={handleApply}
            disabled={loading}
            startIcon={<FilterIcon size={14} aria-hidden />}
          >
            Apply Filter
          </Button>
          <Button
            variant="light-grey"
            type="button"
            onClick={handleReset}
            disabled={loading}
            startIcon={<RotateCcw size={14} aria-hidden />}
          >
            Clear Filter
          </Button>
          <Button
            variant="accent"
            type="button"
            onClick={() => onExport(buildFilters())}
            disabled={loading || exporting}
            startIcon={<Download size={14} aria-hidden />}
          >
            {exporting ? "Exporting..." : "Export"}
          </Button>
        </div>
      </div>
    </Card>
  );
}

export default ExtensionReportFilters;
